import React from 'react'
import { cn } from 'lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import NotesRailSection from './NotesRailSection'
import type { Contributor } from './notesWorkspace.types'

interface NotesRailContributorsProps {
	contributors: Contributor[]
	/** Currently filtered author, if any. */
	activeAuthorId?: string | null
	onSelect: (authorId: string | null) => void
	className?: string
}

const initialsOf = (name: string): string => {
	const parts = name.trim().split(/\s+/)
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
	return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

/**
 * Who is actually contributing, and how much.
 *
 * The proportional bar is what turns this from a list of names into something
 * that reads at a glance: it makes an unbalanced conversation (one person
 * writing everything) visible without anyone counting rows.
 */
const NotesRailContributors: React.FC<NotesRailContributorsProps> = ({
	contributors,
	activeAuthorId,
	onSelect,
	className,
}) => {
	if (contributors.length === 0) return null

	const max = Math.max(...contributors.map(person => person.count), 1)

	return (
		<NotesRailSection
			title="Contributors"
			action={
				activeAuthorId ? (
					<button
						type="button"
						onClick={() => onSelect(null)}
						className="text-xs font-medium text-primary hover:underline"
					>
						Clear
					</button>
				) : undefined
			}
			className={className}
		>
			<ul className="m-0 flex list-none flex-col gap-1 p-0">
				{contributors.map(person => {
					const isActive = activeAuthorId === person.id
					return (
						<li key={person.id}>
							<button
								type="button"
								onClick={() => onSelect(isActive ? null : person.id)}
								aria-pressed={isActive}
								title={person.email}
								data-testid="notes-contributor"
								className={cn(
									'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors',
									isActive ? 'bg-primary/10' : 'hover:bg-muted',
								)}
							>
								<Avatar className="h-7 w-7 shrink-0">
									<AvatarImage src={person.image} alt={person.name} />
									<AvatarFallback className="text-[10px] font-medium">
										{initialsOf(person.name)}
									</AvatarFallback>
								</Avatar>

								<span className="min-w-0 flex-1">
									<span
										className={cn(
											'block truncate text-sm',
											isActive ? 'font-medium text-primary' : 'text-foreground',
										)}
									>
										{person.name}
									</span>
									<span className="mt-1 block h-1 w-full overflow-hidden rounded-full bg-muted">
										<span
											className="block h-full rounded-full bg-primary/60"
											style={{ width: `${Math.round((person.count / max) * 100)}%` }}
										/>
									</span>
								</span>

								<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
									{person.count}
								</span>
							</button>
						</li>
					)
				})}
			</ul>
		</NotesRailSection>
	)
}

export default NotesRailContributors
